import Route from '@ember/routing/route';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import type AbilitiesService from 'ember-can/services/abilities';
import { didCancel, restartableTask, type Task } from 'ember-concurrency';
import type CompanyConfigurationModel from 'garaje/models/company-configuration';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type LoggerService from 'garaje/services/logger';
import type StateService from 'garaje/services/state';
import loadCompanyConfiguration from 'garaje/utils/load-company-configuration';

export interface LocationOverviewVisitorsSettingsRouteModel {
  loadCompanyConfigurationTask: Task<CompanyConfigurationModel | undefined, []>;
}

export default class LocationOverviewVisitorsSettingsRoute extends Route {
  @service declare abilities: AbilitiesService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare logger: LoggerService;
  @service declare router: RouterService;
  @service declare state: StateService;
  @service declare store: Store;

  titleToken = 'Visitors Settings · Envoy';

  beforeModel(): void {
    if (
      this.abilities.cannot('visit-visitor-settings manage-settings') ||
      !this.featureFlags.isEnabled('visitors-no-auto-approvals')
    ) {
      void this.router.transitionTo('unauthorized');
    }
  }

  // Swallows errors because nothing awaits this: a rejection would escape the router's handling.
  loadCompanyConfigurationTask = restartableTask(async () => {
    try {
      return await loadCompanyConfiguration(this.state, this.store);
    } catch (e) {
      if (!didCancel(e)) this.logger.error(<Error>e);
      return undefined;
    }
  });

  // Synchronous so the page paints before the configuration lands. Hands over the task, not the
  // TaskInstance `perform` returns, which is thenable and would make the router await it again.
  model(): LocationOverviewVisitorsSettingsRouteModel {
    void this.loadCompanyConfigurationTask.perform();
    return { loadCompanyConfigurationTask: this.loadCompanyConfigurationTask };
  }
}
